import { useSession } from "@d12labs/auth/client";
import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@d12labs/ui/card";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Pill } from "@d12labs/ui/pill";
import { Skeleton } from "@d12labs/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Flame, Lock, type LucideIcon, Mic, Target } from "lucide-react";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { speechCoachApi } from "../api";
import { computeStreakStats } from "../streak";
import type { AchievementDefinition } from "../types";

// Lucide icon map for achievement icons (client-side only, avoids dynamic imports).
const ACHIEVEMENT_ICONS: Record<string, LucideIcon> = { Mic, Flame, Target };

function AchievementStrip({
	catalog,
	earnedIds,
}: {
	catalog: AchievementDefinition[];
	earnedIds: string[];
}) {
	const earnedSet = new Set(earnedIds);
	return (
		<div className="flex flex-wrap gap-2" data-testid="achievements-strip">
			{catalog.map((a) => {
				const earned = earnedSet.has(a.id);
				const Icon = earned ? (ACHIEVEMENT_ICONS[a.icon] ?? Mic) : Lock;
				return (
					<div
						key={a.id}
						title={a.description}
						className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
							earned
								? "border-primary/30 bg-primary/5 text-primary"
								: "border-hairline bg-surface-1 text-ink-subtle opacity-60"
						}`}
					>
						<Icon className="size-3" />
						{a.title}
					</div>
				);
			})}
		</div>
	);
}

function clientToday(): string {
	return new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD
}

export function TodayPage() {
	const navigate = useNavigate();
	const { data: sessionData } = useSession();
	const userId = sessionData?.user?.id ?? "";

	const planQuery = useQuery({
		queryKey: ["speech-coach", "plan"],
		queryFn: () => speechCoachApi.getPlan(),
	});

	const plan = planQuery.data?.plan ?? null;

	const drillsQuery = useQuery({
		queryKey: ["speech-coach", "drills"],
		queryFn: () => speechCoachApi.drills(),
		enabled: !!plan,
	});

	const sessionsQuery = useQuery({
		queryKey: ["speech-coach", "sessions-streak"],
		queryFn: () => speechCoachApi.listSessions({ query: { pageSize: 100 } }),
		enabled: !!plan,
	});

	const achievementsQuery = useQuery({
		queryKey: ["speech-coach", "achievements"],
		queryFn: () => speechCoachApi.achievements(),
		staleTime: 0,
	});

	// Toast newly-earned achievements once per browser session, deduped via sessionStorage.
	// seenRef guards against React strict-mode double-fire within the same render cycle.
	const seenRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		if (!achievementsQuery.data || !userId) return;
		const storageKey = `speech-coach:seen-achievements:${userId}`;
		const stored: string[] = JSON.parse(sessionStorage.getItem(storageKey) ?? "[]");
		const seenSet = new Set(stored);
		const newIds = achievementsQuery.data.earnedIds.filter(
			(id) => !seenSet.has(id) && !seenRef.current.has(id),
		);
		if (newIds.length === 0) return;
		// Record in-memory first to guard against double-fire.
		for (const id of newIds) seenRef.current.add(id);
		// Persist to sessionStorage so navigating away and back doesn't re-toast.
		sessionStorage.setItem(storageKey, JSON.stringify([...stored, ...newIds]));
		for (const id of newIds) {
			const def = achievementsQuery.data.catalog.find((a) => a.id === id);
			if (def) {
				toast.success(`Achievement unlocked: ${def.title}`, { description: def.description });
			}
		}
	}, [achievementsQuery.data, userId]);

	const today = clientToday();
	const sessionDates =
		sessionsQuery.data?.items.map((s) => new Date(s.createdAt).toLocaleDateString("en-CA")) ?? [];
	const streak = computeStreakStats(sessionDates, today);

	const todaysDrill = drillsQuery.data?.assigned[0] ?? null;

	const streakBadge =
		streak.current > 0 ? (
			<span data-testid="streak-badge">
				<Badge tone="subtle">
					<Flame className="size-3" />
					{streak.current}-day streak
				</Badge>
			</span>
		) : null;

	if (planQuery.isLoading) {
		return (
			<>
				<PageHeader title="Speech Coach" icon={Mic} />
				<PageBody className="p-6 space-y-6 mx-auto max-w-3xl w-full">
					<Skeleton w="100%" h={120} />
					<Skeleton w="100%" h={160} />
				</PageBody>
			</>
		);
	}

	if (!plan) {
		return (
			<>
				<PageHeader
					title="Speech Coach"
					icon={Mic}
					actions={
						<Button variant="ghost" asChild>
							<Link to="/speech-coach/history">History</Link>
						</Button>
					}
				/>
				<PageBody className="p-6 space-y-6 mx-auto max-w-3xl w-full">
					<EmptyState
						icon={Mic}
						title="No active plan yet"
						body="Generate your first 2-week plan from your meeting analyses."
						primary={
							<Button onClick={() => navigate({ to: "/speech-coach/onboarding" })}>
								Generate a plan
							</Button>
						}
					/>
				</PageBody>
			</>
		);
	}

	return (
		<>
			<PageHeader
				title="Speech Coach"
				icon={Mic}
				badges={streakBadge}
				actions={
					<div className="flex items-center gap-2">
						<Button variant="ghost" asChild>
							<Link to="/speech-coach/plan">Plan</Link>
						</Button>
						<Button variant="ghost" asChild>
							<Link to="/speech-coach/history">History</Link>
						</Button>
					</div>
				}
			/>
			<PageBody className="p-6 space-y-6 mx-auto max-w-3xl w-full">
				{/* Plan summary card */}
				<Card>
					<CardHeader>
						<CardTitle>Your active plan</CardTitle>
					</CardHeader>
					<CardContent className="flex flex-col gap-4">
						<div className="flex flex-wrap gap-2">
							{plan.focusDims.map((dim) => (
								<Pill key={dim}>{dim}</Pill>
							))}
						</div>
						<div className="flex items-center gap-2 text-sm text-ink-subtle">
							<span className="font-medium text-foreground">{plan.weeklyTarget}</span>
							<span>sessions / week</span>
						</div>
						{plan.aiBrief ? (
							<p className="text-body-sm text-ink-subtle line-clamp-3">{plan.aiBrief}</p>
						) : null}
						<Button
							variant="ghost"
							size="sm"
							className="self-start"
							onClick={() => navigate({ to: "/speech-coach/plan" })}
						>
							Read more
						</Button>
					</CardContent>
				</Card>

				{/* Achievement strip */}
				{achievementsQuery.data ? (
					<AchievementStrip
						catalog={achievementsQuery.data.catalog}
						earnedIds={achievementsQuery.data.earnedIds}
					/>
				) : null}

				{/* Today's drill card */}
				{drillsQuery.isLoading ? (
					<Skeleton w="100%" h={160} />
				) : todaysDrill ? (
					<Card data-testid="todays-drill-card">
						<CardHeader>
							<div className="flex items-center justify-between">
								<CardTitle>Today's drill</CardTitle>
								<Badge tone="primary">{todaysDrill.category}</Badge>
							</div>
						</CardHeader>
						<CardContent className="flex flex-col gap-4">
							<h3 className="font-medium text-foreground">{todaysDrill.title}</h3>
							<p className="text-body-sm text-ink-subtle line-clamp-3">
								{todaysDrill.instructions}
							</p>
							<Button
								onClick={() =>
									navigate({
										to: "/speech-coach/practice/$drillId",
										params: { drillId: todaysDrill.id },
									})
								}
							>
								Start practice
							</Button>
						</CardContent>
					</Card>
				) : (
					<Card>
						<CardContent className="py-8">
							<EmptyState
								small
								title="No drills assigned"
								body="Your plan has no assigned drills yet."
							/>
						</CardContent>
					</Card>
				)}
			</PageBody>
		</>
	);
}
